import React, { useState, useRef, useEffect } from 'react';
import ProductCard from './ProductCard';

const ProductSlider = ({ products, onPreview, onEdit, onDelete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const sliderRef = useRef(null);
  const autoScrollInterval = useRef(null);

  // Auto-scroll functionality
  useEffect(() => {
    if (isAutoScrolling && products.length > 1) {
      autoScrollInterval.current = setInterval(() => {
        if (sliderRef.current) {
          sliderRef.current.scrollBy({ left: 300, behavior: 'smooth' });
          setCurrentIndex(prev => {
            const newIndex = prev >= products.length - 1 ? 0 : prev + 1;
            return newIndex;
          });
        }
      }, 3000); // Auto-scroll every 3 seconds
    } else {
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
      }
    }

    return () => {
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
      }
    };
  }, [isAutoScrolling, products.length]);

  // Pause auto-scroll on user interaction
  const pauseAutoScroll = () => {
    setIsAutoScrolling(false);
    // Resume auto-scroll after 5 seconds of inactivity
    setTimeout(() => {
      setIsAutoScrolling(true);
    }, 5000);
  };

  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -300, behavior: 'smooth' });
      setCurrentIndex(Math.max(0, currentIndex - 1));
      pauseAutoScroll();
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 300, behavior: 'smooth' });
      setCurrentIndex(prev => {
        const newIndex = prev >= products.length - 1 ? 0 : prev + 1;
        return newIndex;
      });
      pauseAutoScroll();
    }
  };

  const canScrollLeft = currentIndex > 0;
  const canScrollRight = currentIndex < products.length - 1;

  const handleIndicatorClick = (index) => {
    setCurrentIndex(index);
    if (sliderRef.current) {
      const slideWidth = 300 + 24; // card width + gap
      sliderRef.current.scrollTo({
        left: index * slideWidth,
        behavior: 'smooth'
      });
    }
    pauseAutoScroll();
  };

  return (
    <div className="relative">
      {/* Left Arrow */}
      {canScrollLeft && (
        <button
          onClick={scrollLeft}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 -ml-6 p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
          style={{backgroundColor: '#FFFFFF', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'}}
        >
          <span className="material-symbols-outlined" style={{color: '#C8A96A'}}>
            chevron_left
          </span>
        </button>
      )}

      {/* Right Arrow */}
      {canScrollRight && (
        <button
          onClick={scrollRight}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 -mr-6 p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
          style={{backgroundColor: '#FFFFFF', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'}}
        >
          <span className="material-symbols-outlined" style={{color: '#C8A96A'}}>
            chevron_right
          </span>
        </button>
      )}

      {/* Auto-scroll Indicator */}
      <div className="absolute top-0 right-0 z-10">
        <button
          onClick={() => setIsAutoScrolling(!isAutoScrolling)}
          className="p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-md"
          title={isAutoScrolling ? "Pause auto-scroll" : "Resume auto-scroll"}
        >
          <span className="material-symbols-outlined text-sm" style={{color: '#C8A96A'}}>
            {isAutoScrolling ? 'pause' : 'play_arrow'}
          </span>
        </button>
      </div>

      {/* Slider Container */}
      <div className="overflow-hidden">
        <div
          ref={sliderRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none'}}
          onMouseEnter={pauseAutoScroll}
        >
          {products.map((product) => (
            <div key={product.id} className="flex-none w-[300px] sm:w-[350px] md:w-[400px]">
              <ProductCard
                product={product}
                onPreview={onPreview}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Progress Indicators */}
      <div className="flex justify-center gap-2 mt-6">
        {products.map((_, index) => (
          <button
            key={index}
            onClick={() => handleIndicatorClick(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? 'w-8'
                : 'bg-stone-300 hover:bg-stone-400'
            }`}
            style={{
              backgroundColor: index === currentIndex ? '#C8A96A' : undefined
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductSlider;
